import React from 'react';
import { HiPaperClip, HiXMark } from 'react-icons/hi2';
import { BaseModal } from '../../../../components/Modal/BaseModal';
import { Button } from '../../../../components/Button';
import { Textarea } from '../../../../components/Textarea';

export interface AttachFilesModalProps {
  isOpen: boolean;
  isMinimized: boolean;
  isLoading: boolean;
  attachments: File[];
  attachmentComments: Record<string, string>;
  comment: string;
  isDragOver: boolean;
  onMinimize: () => void;
  onRestore: () => void;
  onClose: () => void;
  onConfirm: (comment: string, attachments: File[], attachmentComments: Record<string, string>) => void;
  onCommentChange: (comment: string) => void;
  onAttachmentCommentChange: (fileKey: string, comment: string) => void;
  onAddFiles: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveFile: (index: number) => void;
  onDragEnter: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
}

/**
 * Modal para anexar arquivos com comentários à demanda
 */
export const AttachFilesModal: React.FC<AttachFilesModalProps> = ({
  isOpen,
  isMinimized,
  isLoading,
  attachments,
  attachmentComments,
  comment,
  isDragOver,
  onMinimize,
  onRestore,
  onClose,
  onConfirm,
  onCommentChange,
  onAttachmentCommentChange,
  onAddFiles,
  onRemoveFile,
  onDragEnter,
  onDragLeave,
  onDragOver,
  onDrop,
}) => {
  return (
    <BaseModal
      isOpen={isOpen}
      isMinimized={isMinimized}
      modalId="demand-attach"
      actionIcon={<HiPaperClip className="w-4 h-4" />}
      toolbarActionId="attach"
      onMinimize={onMinimize}
      onRestore={onRestore}
      onClose={onClose}
      title="Anexar Arquivos"
      description="Adicione arquivos em anexo à demanda. Você pode adicionar um comentário opcional para explicar os anexos."
      useStandardLayout={true}
      size="lg"
      resizable={true}
      stickyFooter={true}
      footerContent={
        <div className="flex justify-end space-x-3 p-4">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
            className="min-w-20"
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={() => onConfirm(comment, attachments, attachmentComments)}
            disabled={isLoading || (attachments.length === 0 && !comment?.trim())}
            isLoading={isLoading}
            className="min-w-20"
          >
            {isLoading ? 'Enviando...' : attachments.length > 0 ? 'Anexar' : 'Comentar'}
          </Button>
        </div>
      }
    >
      <div className="p-4 space-y-4">


        {/* Anexos - SEÇÃO PRINCIPAL */}
        <div className="space-y-3">
          <div className="flex items-center space-x-2">
            <label className="block text-lg font-semibold text-gray-900 dark:text-gray-100">
              📎 Selecionar Arquivos
            </label>
            <span className="text-sm text-gray-500">(principal)</span>
          </div>
          
          {/* Upload de arquivos - ÁREA DESTACADA */}
          <div 
            className={`relative border-2 border-dashed rounded-lg p-6 transition-all duration-300 cursor-pointer group ${
              isDragOver 
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/30 shadow-lg scale-[1.02]' 
                : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 bg-gray-50/50 dark:bg-gray-800/50 hover:bg-gray-100/70 dark:hover:bg-gray-700/70'
            }`}
            onDragEnter={onDragEnter}
            onDragLeave={onDragLeave}
            onDragOver={onDragOver}
            onDrop={onDrop}
          >
            <input
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.xls,.xlsx,.txt,.zip,.rar"
              onChange={onAddFiles}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              disabled={isLoading}
            />
            <div className="flex flex-col items-center justify-center text-center space-y-3">
              <div className={`p-3 rounded-full transition-all duration-300 ${
                isDragOver 
                  ? 'bg-blue-100 dark:bg-blue-800 scale-110' 
                  : 'bg-gray-100 dark:bg-gray-700 group-hover:bg-gray-200 dark:group-hover:bg-gray-600'
              }`}>
                <HiPaperClip className={`w-6 h-6 transition-colors ${
                  isDragOver 
                    ? 'text-blue-600 dark:text-blue-400' 
                    : 'text-gray-500 dark:text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300'
                }`} />
              </div>
              <div>
                <p className={`text-base font-medium transition-colors ${
                  isDragOver 
                    ? 'text-blue-700 dark:text-blue-300' 
                    : 'text-gray-700 dark:text-gray-300 group-hover:text-gray-800 dark:group-hover:text-gray-200'
                }`}>
                  {isDragOver 
                    ? 'Solte os arquivos aqui!' 
                    : attachments.length > 0 
                      ? 'Adicionar mais arquivos' 
                      : 'Clique ou arraste arquivos aqui'
                  }
                </p>
                <p className={`text-sm mt-1 transition-colors ${
                  isDragOver 
                    ? 'text-blue-600 dark:text-blue-400' 
                    : 'text-gray-500 dark:text-gray-400'
                }`}>
                  PDF, Imagens, Word, Excel, TXT, ZIP - Máx. 30MB cada
                </p>
              </div>
            </div>
          </div>

          {/* Lista de arquivos */}
          {attachments.length > 0 && (
            <div className="bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
              <div className="flex items-center space-x-2 mb-2">
                <HiPaperClip className="w-3 h-3 text-gray-600 dark:text-gray-400" />
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                  {attachments.length} arquivo{attachments.length > 1 ? 's' : ''} selecionado{attachments.length > 1 ? 's' : ''}
                </span>
              </div>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {attachments.map((file, index) => {
                  const fileKey = Object.keys(attachmentComments).find(key => 
                    key.startsWith(`${file.name}_${file.size}`)
                  ) || `${file.name}_${file.size}_${index}`;
                  
                  return (
                    <div key={index} className="border border-gray-200 dark:border-gray-600 rounded-lg p-3 bg-white dark:bg-gray-800">
                      {/* Header do arquivo */}
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2 min-w-0 flex-1">
                          <HiPaperClip className="w-4 h-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">
                            {file.name}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
                            ({(file.size / 1024).toFixed(1)} KB)
                          </span>
                        </div>
                        <button
                          onClick={() => onRemoveFile(index)}
                          className="ml-2 p-1 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-full transition-all duration-200 flex-shrink-0"
                          title="Remover arquivo"
                          disabled={isLoading}
                        >
                          <HiXMark className="w-3 h-3 text-red-500" />
                        </button>
                      </div>
                      
                      {/* Campo de comentário individual */}
                      <div className="space-y-1">
                        <div className="flex items-center justify-between">
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">
                            Comentário para este arquivo (opcional)
                          </label>
                          <span className={`text-xs ${
                            (attachmentComments[fileKey] || '').length > 200 
                              ? 'text-red-500 font-medium' 
                              : (attachmentComments[fileKey] || '').length > 180 
                                ? 'text-yellow-600 font-medium' 
                                : 'text-gray-400'
                          }`}>
                            {(attachmentComments[fileKey] || '').length}/200
                          </span>
                        </div>
                        <textarea
                          value={attachmentComments[fileKey] || ''}
                          onChange={(e) => {
                            const newValue = e.target.value;
                            if (newValue.length <= 200) {
                              onAttachmentCommentChange(fileKey, newValue);
                            }
                          }}
                          placeholder={`Descreva o conteúdo de "${file.name}" (máx. 200 caracteres)...`}
                          rows={2}
                          className={`w-full px-2 py-1.5 text-sm border rounded 
                            bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                            focus:ring-1 resize-none transition-colors ${
                            (attachmentComments[fileKey] || '').length > 200
                              ? 'border-red-300 dark:border-red-600 focus:ring-red-500 focus:border-red-500'
                              : (attachmentComments[fileKey] || '').length > 180
                                ? 'border-yellow-300 dark:border-yellow-600 focus:ring-yellow-500 focus:border-yellow-500'
                                : 'border-gray-300 dark:border-gray-600 focus:ring-indigo-500 focus:border-indigo-500'
                          }`}
                          disabled={isLoading}
                        />
                        {(attachmentComments[fileKey] || '').length > 180 && (
                          <p className={`text-xs ${
                            (attachmentComments[fileKey] || '').length > 200
                              ? 'text-red-500'
                              : 'text-yellow-600'
                          }`}>
                            {(attachmentComments[fileKey] || '').length > 200
                              ? 'Limite de caracteres excedido!'
                              : `Restam ${200 - (attachmentComments[fileKey] || '').length} caracteres`
                            }
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Comentário Opcional - SEÇÃO SECUNDÁRIA */}
        <div className="space-y-2 pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-2">
            <label className="block text-sm font-medium text-gray-600 dark:text-gray-400">
              💬 Comentário Adicional
            </label>
            <span className="text-xs text-gray-400">(opcional)</span>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-500 mb-2">
            Adicione um comentário geral sobre os anexos, se necessário.
          </p>
          <Textarea
            variant="rich"
            value={comment}
            onChange={onCommentChange}
            placeholder="Digite um comentário geral sobre os anexos (opcional)..."
            disabled={isLoading}
            className="w-full"
            rows={3}
          />
        </div>
      </div>
    </BaseModal>
  );
};
